import { useTranslation } from "react-i18next";
import { Button, Select, SelectItem, TextInput, Tile } from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import { ORDER_TYPES, OfferResponse } from "../../types";
import { currencyFormatter, fileText, formatDate } from "../../utils";
import { CURRENCY_LIST } from "../../utils/enums";
import FileReader from "../../components/FileReader/FileReader";
import InversePositionTag from "./InversePositionTag";
import "./bid.scss";

export type SellerOfferLineProps = {
  offer: OfferResponse;
  /** Show the live inverse-auction bid input (INVERSE / INVERSE_JOINT + order OPEN). */
  isInverse: boolean;
  isDeclined: boolean;
  /** Whether the normal (non-inverse) price/currency edit affordance is allowed. */
  canEditNormalOffer: boolean;
  isEditing: boolean;
  editPrice: string;
  editCurrency: string;
  /** Disables the edit inputs while a per-line update is in flight (joint). */
  editDisabled?: boolean;
  inverseError?: string;
  inversePlaceholder?: string;
  onInverseChange?: (value: string) => void;
  onStartEdit?: () => void;
  onEditPriceChange?: (value: string) => void;
  onEditCurrencyChange?: (value: string) => void;
  /** Whether another line is currently being edited (joint) — disables the pencil. */
  startEditDisabled?: boolean;
};

/**
 * One material line of a seller's own offer (Task 6) — name/quantity/price/total, with the price
 * cell switching between: the live inverse-auction bid input (OPEN inverse orders), the
 * price+currency edit inputs (OPEN normal orders), or the read-only price with a pencil to enter
 * edit mode. Shared by `SellerOffer.tsx` (single line) and `JointSellerOffer.tsx` (one per line);
 * all state lives in the parent, this is a pure presentational cell. Replaces the pre-Carbon
 * Tailwind grid + raw `<input>`/`<select>` with Carbon `TextInput`/`Select`.
 */
const SellerOfferLine = ({
  offer,
  isInverse,
  isDeclined,
  canEditNormalOffer,
  isEditing,
  editPrice,
  editCurrency,
  editDisabled = false,
  inverseError,
  inversePlaceholder,
  onInverseChange,
  onStartEdit,
  onEditPriceChange,
  onEditCurrencyChange,
  startEditDisabled = false,
}: SellerOfferLineProps) => {
  const { t } = useTranslation();
  const totalAmount = (offer?.price || 0) * (offer?.order?.quantity || 0);
  const isInverseAuction =
    offer?.order?.type === ORDER_TYPES.INVERSE ||
    offer?.order?.type === ORDER_TYPES.INVERSE_JOINT;
  const itemFiles = offer?.order?.itemFiles || [];

  const renderPriceCell = () => {
    if (isInverse) {
      return (
        <div className="seller-line__price-edit">
          <span>{currencyFormatter(offer?.price || 0, offer?.currency)}</span>
          <TextInput
            id={`bid-${offer?.id}`}
            size="sm"
            type="number"
            hideLabel
            labelText={t("offer.columns.yourBid")}
            disabled={isDeclined}
            max={inversePlaceholder}
            placeholder={inversePlaceholder}
            invalid={!!inverseError}
            invalidText={inverseError}
            onChange={(e) => onInverseChange?.(e.target.value)}
          />
          <span className="seller-line__unit">
            {offer?.order?.currency} / {offer?.order?.measurementUnit}
          </span>
        </div>
      );
    }

    if (isEditing && canEditNormalOffer) {
      return (
        <div className="seller-line__price-edit">
          <span>{currencyFormatter(offer?.price || 0, offer?.currency)}</span>
          <TextInput
            id={`price-${offer?.id}`}
            size="sm"
            type="number"
            hideLabel
            labelText={t("offer.columns.yourPrice")}
            step="0.01"
            min="0"
            disabled={editDisabled || isDeclined}
            value={editPrice}
            placeholder={String(offer?.price || 0)}
            onChange={(e) => onEditPriceChange?.(e.target.value)}
          />
          <Select
            id={`currency-${offer?.id}`}
            size="sm"
            hideLabel
            labelText={t("offer.columns.yourPrice")}
            disabled={editDisabled || isDeclined}
            value={editCurrency}
            onChange={(e) => onEditCurrencyChange?.(e.target.value)}
          >
            {CURRENCY_LIST.map((c) => (
              <SelectItem key={c.id} value={c.name} text={c.name} />
            ))}
          </Select>
          <span className="seller-line__unit">
            / {offer?.order?.measurementUnit}
          </span>
        </div>
      );
    }

    return (
      <div className="seller-line__price-edit">
        <span>
          {currencyFormatter(offer?.price || 0, offer?.currency)}{" "}
          {offer?.order?.measurementUnit}
        </span>
        {canEditNormalOffer && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Edit}
            iconDescription={t("cta.edit")}
            tooltipPosition="left"
            disabled={startEditDisabled}
            onClick={onStartEdit}
          />
        )}
      </div>
    );
  };

  return (
    <Tile className="seller-line">
      <div className="seller-line__grid">
        <div className="seller-line__cell seller-line__name">
          <h4>{offer?.order?.material?.globalMaterialName}</h4>
          {isInverseAuction && !isDeclined && offer?.order?.id && (
            <InversePositionTag orderId={offer.order.id} />
          )}
          {(offer?.order?.startSupplyDate || offer?.order?.endSupplyDate) && (
            <p className="seller-line__supply">
              {t("offer.buyerSupplyPeriod")}:{" "}
              {formatDate(offer?.order?.startSupplyDate ?? "")}
              {offer?.order?.endSupplyDate
                ? ` - ${formatDate(offer?.order?.endSupplyDate)}`
                : ""}
            </p>
          )}
          {itemFiles.length > 0 && (
            <div className="seller-line__files">
              {itemFiles.map((file) => (
                <FileReader
                  key={file.fileURL}
                  fileKey={file.fileURL}
                  text={fileText(file.type)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="seller-line__cell seller-line__center">
          {offer?.order?.quantity || 0} {offer?.order?.measurementUnit}
        </div>

        <div className="seller-line__cell seller-line__center">
          {renderPriceCell()}
        </div>

        <div className="seller-line__cell seller-line__total">
          {currencyFormatter(totalAmount, offer?.currency)}
        </div>
      </div>
    </Tile>
  );
};

export default SellerOfferLine;
